import React from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Gift, ArrowRight, MapPin } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { useReglages } from "../lib/useReglages";
import { libelleFenetre, prochaineFenetre, fenetreEnCours } from "../lib/dates";
import INSTANTANE from "../lib/portesOuvertes.build.json";

// Dates figées au moment de la mise en ligne, écrites par scripts/generate-static-seo.js
// depuis le classeur de l'école. Elles servent à deux choses : le HTML brut lu par les
// robots (qui n'exécutent pas JavaScript) et l'affichage immédiat avant la réponse de
// l'API. Dès que l'API répond, c'est elle qui fait foi.
const aVenir = (fenetres) => {
  const jour = new Date().toISOString().slice(0, 10);
  return (fenetres || []).filter((f) => f.fin >= jour);
};

const LIEU = "Chemin des Dailles 10E, 1053 Cugy VD";

export default function PortesOuvertes() {
  const reglages = useReglages();
  const duClasseur = aVenir(reglages.portes_ouvertes);
  const fenetres = duClasseur.length ? duClasseur : aVenir(INSTANTANE.portes_ouvertes);
  const prix = reglages.prix_essai ?? INSTANTANE.prix_essai ?? 10;
  const prochaine = prochaineFenetre(fenetres);
  const enCours = fenetreEnCours(fenetres);

  const listeDates = fenetres.map((f) => libelleFenetre(f)).join(" · ");
  useSEO({
    title: "Portes Ouvertes — cours d'essai offerts · 1000 ARTS Cugy VD",
    description: `Dates des Portes Ouvertes de 1000 ARTS, école de danse à Cugy VD : tous les cours d'essai sont offerts pendant ces périodes. ${listeDates}`,
    canonical: "/portes-ouvertes",
    jsonLd: fenetres.map((f) => ({
      "@context": "https://schema.org",
      "@type": "Event",
      name: "Portes Ouvertes 1000 ARTS — cours d'essai offerts",
      description:
        "Tous les cours de danse, chant et comédie musicale de 1000 ARTS peuvent être essayés gratuitement pendant les Portes Ouvertes, sans engagement.",
      startDate: f.debut,
      endDate: f.fin,
      eventSchedule: { "@type": "Schedule", byDay: "https://schema.org/PublicHolidays" },
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      isAccessibleForFree: true,
      url: "https://1000arts.ch/portes-ouvertes",
      location: {
        "@type": "Place",
        name: "1000 ARTS",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Chemin des Dailles 10E",
          postalCode: "1053",
          addressLocality: "Cugy",
          addressRegion: "VD",
          addressCountry: "CH",
        },
      },
      organizer: { "@type": "DanceSchool", name: "1000 ARTS", url: "https://1000arts.ch" },
      offers: {
        "@type": "Offer",
        price: 0,
        priceCurrency: "CHF",
        availability: "https://schema.org/InStock",
        url: "https://1000arts.ch/inscription",
      },
    })),
  });

  return (
    <div className="pt-28 pb-24 px-6" data-testid="portes-ouvertes-page">
      <div className="max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em]"
             style={{ color: "var(--primary)" }}>
          <Gift size={14} /> Cours d'essai offerts
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">
          Portes Ouvertes
        </h1>
        <p className="text-base md:text-lg text-[var(--muted)] mt-5 leading-relaxed">
          Plusieurs fois par saison, 1000 ARTS ouvre tous ses cours&nbsp;: danse, chant, comédie
          musicale. Pendant ces périodes, <strong>l'essai est offert</strong>, sans
          engagement et sans inscription préalable — il suffit de réserver sa place.
        </p>

        {enCours && (
          <div className="rounded-2xl p-4 mt-6 text-sm font-semibold"
               style={{ background: "#DCFCE7", border: "1px solid #86EFAC", color: "#065F46" }}
               data-testid="portes-ouvertes-en-cours">
            C'est en ce moment&nbsp;: les essais sont gratuits jusqu'au {libelleFenetre(enCours).split(" au ")[1]}.
          </div>
        )}

        <h2 className="font-display text-lg md:text-xl mt-12 tracking-tight">
          Prochaines dates
        </h2>
        {fenetres.length > 0 ? (
          <ul className="mt-4 space-y-3" data-testid="portes-ouvertes-liste">
            {fenetres.map((f) => (
              <li
                key={f.debut}
                className="flex items-start gap-3 rounded-2xl p-4"
                style={{ background: "var(--primary-tint)" }}
              >
                <CalendarDays size={18} style={{ color: "var(--primary)", marginTop: 2 }} />
                <span className="text-[var(--text)]">
                  Portes Ouvertes <strong>{libelleFenetre(f)}</strong>
                  {prochaine && prochaine.debut === f.debut && !enCours && (
                    <span className="text-[var(--muted)]"> — la prochaine</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-base text-[var(--muted)] mt-4 leading-relaxed">
            Si aucune date n'apparaît ici, celles de la saison suivante ne sont pas encore
            publiées&nbsp;: écris-nous à info1000arts@gmail.com et nous te préviendrons dès
            qu'elles le sont.
          </p>
        )}

        <h2 className="font-display text-lg md:text-xl mt-12 tracking-tight">
          En dehors de ces périodes
        </h2>
        <p className="text-base text-[var(--muted)] mt-4 leading-relaxed">
          Un cours d'essai reste possible toute l'année&nbsp;: il est alors facturé
          <strong> CHF {prix}.–</strong>, payables sur place (Twint ou cash). Ce montant n'est ni
          remboursé ni déduit de la facture d'inscription. Les cours d'essai
          suivent le planning habituel, et l'école confirme le créneau par e-mail.
        </p>
        <p className="text-base text-[var(--muted)] mt-4 leading-relaxed">
          Une exception&nbsp;: les cours qui participent au spectacle de fin d'année n'acceptent
          plus de cours d'essai <strong>dès le 1<sup>er</sup> février</strong>, car les
          chorégraphies sont en place et les répétitions ne peuvent plus être interrompues. Pour
          ces cours, la prochaine occasion d'essayer est la période de Portes Ouvertes de juin.
        </p>

        <p className="text-base mt-6">
          <Link to="/dates-importantes" data-testid="po-lien-dates" className="underline" style={{ color: "var(--primary)" }}>
            Voir toutes les dates importantes de la saison
          </Link>
        </p>

        <h2 className="font-display text-lg md:text-xl mt-12 tracking-tight">Où&nbsp;?</h2>
        <p className="text-base text-[var(--muted)] mt-4 leading-relaxed flex items-start gap-2">
          <MapPin size={18} style={{ marginTop: 3 }} />
          <span>{LIEU} — à 10 minutes de Lausanne et 5 minutes d'Echallens, parking devant le
            bâtiment.</span>
        </p>

        <div className="flex flex-wrap gap-3 mt-10">
          <Link to="/inscription" className="btn-primary" data-testid="portes-ouvertes-cta-essai">
            Réserver un cours d'essai <ArrowRight size={16} />
          </Link>
          <Link to="/planning" className="btn-ghost" data-testid="portes-ouvertes-cta-planning">
            Voir le planning
          </Link>
        </div>
      </div>
    </div>
  );
}
